import KeyHelper from "@/common/components/KeyHelper";
import Modal from "@/common/components/Modal";
import TextField from "@/common/components/TextField";
import { searchableModels } from "@/common/constants/searchableModels.constant";
import { ArrowDown, ArrowUp, CommandIcon, SearchIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useFuzzySearchList } from '@nozbe/microfuzz/react';
import { SearchableModel } from "@/common/types/searchableModel.type";
import Badge from "@/common/components/Badge";
import ListFromSelectedModel from "./ListFromSelectedModel";
import ListModels from "./ListModels";
import IconButton from "@/common/components/IconButton";
import { useInternalRouter } from "@/common/stores/internalRouter";

interface QuickSearchProps {
  collapsed?: boolean;
}

const QuickSearch = ({ collapsed }: QuickSearchProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const { clearPaths } = useInternalRouter();

  const searchFieldRef = useRef<HTMLInputElement>(null);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedModel, setSelectedModel] = useState<SearchableModel>();
  const [search, setSearch] = useState('');

  const isMac = useMemo(() => {
    return /Macintosh|MacIntel|MacPPC|Mac68K/.test(window.navigator.platform) ||
      /Mac OS X/.test(window.navigator.userAgent);
  }, []);

  const resultSearchableModelsSearch = useFuzzySearchList({
    list: searchableModels,
    key: 'label',
    queryText: search,
    mapResultItem: ({ item }) => item,
  });

  const onToggleModal = (value: boolean) => {
    setSearch('');
    setSelectedModel(undefined);
    setSelectedIndex(0);
    setIsOpen(value);
  };

  const onSelectModel = (index: number) => {
    setSelectedModel(resultSearchableModelsSearch[index]);
    setSearch('');
    setSelectedIndex(0);

    searchFieldRef.current?.focus();
  };

  const onUnselectModel = () => {
    setSelectedModel(undefined);
    setSearch('');
    setSelectedIndex(0);

    searchFieldRef.current?.focus();
  };

  const onSelectModelItem = (callback: () => void) => {
    onToggleModal(false);
    clearPaths();

    callback();
  };

  const handleKeyboard = (
    onArrowDown: (index: number) => number,
    onSelect: (index: number) => void,
  ) => (event: KeyboardEvent) => {
    setSelectedIndex((prev) => {
      const keyCode = event.code;

      if (keyCode === 'ArrowLeft' && !search) {
        if (selectedModel) {
          onUnselectModel();
        } else {
          onToggleModal(false);
        }
      }

      if (keyCode === 'ArrowUp' && prev > 0) {
        return prev - 1;
      }

      if (keyCode === 'ArrowDown') {
        return onArrowDown(prev);
      }

      if (keyCode === 'Enter' || keyCode === 'ArrowRight') {
        onSelect(prev);
        return 0;
      }

      return prev;
    });
  };

  const handleKeyScroll = (listElement: HTMLDivElement, selectedItemElement: HTMLDivElement) => {
    if (listElement && selectedItemElement) {
      const listElementRect = listElement.getBoundingClientRect();
      const selectedItemRect = selectedItemElement.getBoundingClientRect();

      if (selectedItemRect.bottom > listElementRect.bottom) {
        listElement.scrollTo(listElement.scrollLeft, listElement.scrollTop + selectedItemRect.height + 4);
      }

      if (selectedItemRect.top < listElementRect.top) {
        listElement.scrollTo(listElement.scrollLeft, listElement.scrollTop - selectedItemRect.height - 4);
      }

      if (listElementRect.top > (selectedItemRect.bottom - 4) || listElementRect.bottom < (selectedItemRect.top - 4)) {
        listElement.scrollTo(listElement.scrollLeft, selectedItemRect.y - 4);
      }
    }
  };

  const handleOpenByKeyboard = (event: KeyboardEvent) => {
    if (isOpen) {
      return;
    }

    if (!isMac && event.ctrlKey && event.code === 'KeyK') {
      event.preventDefault();
      onToggleModal(true);
    }

    if (isMac && event.metaKey && !event.ctrlKey && event.code === 'KeyK') {
      event.preventDefault();
      onToggleModal(true);
    }
  };

  useEffect(() => {
    document.removeEventListener('keydown', handleOpenByKeyboard, true);
    document.addEventListener('keydown', handleOpenByKeyboard, true);
  }, []);

  return (
    <>
      <div onClick={() => setIsOpen(true)}>
        {collapsed ? (
          <IconButton Icon={<SearchIcon strokeWidth={1} />} />
        ) : (
          <>
            <button
              className="flex items-center gap-2.5 bg-neutral-op-8 p-4 px-3 w-full rounded-md"
            >
              <SearchIcon
                size="18px"
              />

              <div className="flex-1 flex items-center justify-between gap-2">
                <p className="text-sm text-white-400 leading-none">
                  Pesquisar
                </p>

                <div className="bg-white-200 px-1.5 rounded-sm flex items-center gap-1 font-mono text-[12px] text-white-400 font-semibold">
                  <span>
                    {isMac ? <CommandIcon size={12} strokeWidth={2} className="mb-px" /> : 'Ctrl'}
                  </span>

                  <span>
                    +
                  </span>

                  <span>
                    K
                  </span>
                </div>
              </div>
            </button>
          </>
        )}
      </div>

      <Modal
        isOpen={isOpen}
        setIsOpen={onToggleModal}
        className="bg-black-900 max-w-none w-[calc(100vw-40%)] h-[500px] overflow-hidden"
        body={(
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-white-400">Navegar</p>

                    <KeyHelper><ArrowUp className="size-4" /></KeyHelper>
                    <KeyHelper><ArrowDown className="size-4" /></KeyHelper>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-white-400">Selecionar</p>

                    <KeyHelper>Enter</KeyHelper>
                  </div>

                  <div className="flex items-center justify-between gap-2">
                    <p className="text-white-400">Fechar</p>

                    <KeyHelper>Esc</KeyHelper>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <TextField
                  ref={searchFieldRef}
                  LeftIcon={<SearchIcon />}
                  placeholder="Pesquisar"
                  className="rounded-sm"
                  value={search}
                  onChange={(e) => {
                    setSelectedIndex(0);
                    setSearch(e.currentTarget.value);
                  }}
                />

                {selectedModel && (
                  <Badge
                    Icon={selectedModel.icon}
                    text={selectedModel.label}
                    onDelete={() => onUnselectModel()}
                    className="rounded-sm flex bg-neutral-op-8 w-fit flex-row-reverse overflow-hidden !p-0 gap-0"
                    overwriteClassName
                    deleteClassName="bg-black p-2"
                    contentClassName="px-3 py-2 text-white-400"
                    deleteIconColor="var(--foreground)"
                    iconProps={{ size: '20px' }}
                  />
                )}
              </div>
            </div>

            <div>
              {!selectedModel && (
                <ListModels
                  enabled={isOpen && !selectedModel}
                  search={search}
                  models={resultSearchableModelsSearch}
                  onSelect={onSelectModel}
                  selectedIndex={selectedIndex}
                  handleKeyboard={handleKeyboard}
                  handleKeyScroll={handleKeyScroll}
                />
              )}

              {selectedModel && (
                <ListFromSelectedModel
                  enabled={!!(isOpen && selectedModel)}
                  search={search}
                  model={selectedModel}
                  onSelect={onSelectModelItem}
                  selectedIndex={selectedIndex}
                  handleKeyboard={handleKeyboard}
                  handleKeyScroll={handleKeyScroll}
                />
              )}
            </div>
          </div>
        )}
      />
    </>
  );
};

export default QuickSearch;
